import type { ICellRendererParams } from 'ag-grid-enterprise';
import { memo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TextColumnParams } from '../BlotterTable/columns/text';
import { EllipsisBoxWithTooltip } from '../Form/BaseSelect/EllipsisBoxWithTooltip';
import { Text } from '../Text';

export type AgGridTextParams = {
  columnParams: TextColumnParams;
  /** When true, render `columnParams.placeholder` when the formatted display is empty (e.g. `textLiveEdit`). */
  showPlaceholderWhenEmpty?: boolean;
};

function isDisplayEmpty(formatted: string | undefined | null): boolean {
  if (formatted == null || formatted === '') {
    return true;
  }
  return formatted.trim() === '';
}

function AgGridTextInner(props: ICellRendererParams & AgGridTextParams): ReactNode {
  if (!props.column || !props.node) {
    return null;
  }

  const { columnParams, showPlaceholderWhenEmpty } = props;
  const formatted = props.valueFormatted ?? '';
  const { placeholder, suppressEllipsize, suppressOverflowTooltip } = columnParams;

  if (showPlaceholderWhenEmpty && placeholder != null && isDisplayEmpty(formatted)) {
    const placeholderColor = 'colorTextMuted';
    return suppressEllipsize ? (
      <Text color={placeholderColor}>{placeholder}</Text>
    ) : (
      <EllipsisBoxWithTooltip
        tooltipProps={{ placement: 'bottom-start' }}
        color={placeholderColor}
        suppressOverflowTooltip={suppressOverflowTooltip}
      >
        <Text color={placeholderColor}>{placeholder}</Text>
      </EllipsisBoxWithTooltip>
    );
  }

  const textColor =
    props.column.isCellEditable(props.node) && placeholder != null && !props.valueFormatted
      ? (columnParams.color ?? 'colorTextSubtle')
      : columnParams.color;

  const href = columnParams.getHref?.(props as ICellRendererParams & TextColumnParams);
  const content = formatted;
  const linkStyle = { color: textColor, textDecoration: 'underline' };

  const renderContent = (children: ReactNode) => {
    if (href) {
      return (
        <Link to={href} style={linkStyle}>
          {children}
        </Link>
      );
    }
    return <Text color={textColor}>{children}</Text>;
  };

  return suppressEllipsize ? (
    renderContent(content)
  ) : (
    <EllipsisBoxWithTooltip
      tooltipProps={{ placement: 'bottom-start' }}
      color={textColor}
      suppressOverflowTooltip={suppressOverflowTooltip}
    >
      {renderContent(content)}
    </EllipsisBoxWithTooltip>
  );
}

export const AgGridText = memo(AgGridTextInner);
